import { Injectable } from '@angular/core';
import { QuestionView, QuestionType } from '../models';

/**
 * Service to handle question numbering and section ID calculations
 */
@Injectable({
  providedIn: 'root'
})
export class QuestionNumberingService {
  /**
   * Find the section ID for a question at a given index
   * Looks backwards from the current position to find the most recent Section
   */
  findSectionId(questions: QuestionView[], index: number): string | undefined {
    for (let i = index - 1; i >= 0; i--) {
      if (questions[i].questionType === QuestionType.Section) {
        return questions[i].id;
      }
    }
    return undefined;
  }

  /**
   * Recalculate question numbers and section IDs for all questions
   * 
   * - Section elements don't have sectionId (they are sections themselves)
   * - Title and HelperText elements get sectionId but no questionNumber
   * - Regular questions get both sectionId and questionNumber
   */
  recalculateQuestionNumbers(questions: QuestionView[]): QuestionView[] {
    if (!questions || questions.length === 0) {
      return [];
    }
    
    // Sort questions by order FIRST before finding sections
    const sortedQuestions = [...questions].sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });
    
    let questionCounter = 1;
    return sortedQuestions.map((q, index) => {
      // Section elements don't have sectionId (they are sections themselves)
      if (q.questionType === QuestionType.Section) {
        return { ...q, order: index, questionNumber: undefined, sectionId: undefined };
      }
      
      // Title and HelperText elements get sectionId but no questionNumber
      if (q.questionType === QuestionType.Title || q.questionType === QuestionType.HelperText) {
        const sectionId = this.findSectionId(sortedQuestions, index);
        return {
          ...q,
          order: index,
          questionNumber: undefined,
          sectionId: sectionId
        };
      }
      
      // Actual questions get order, questionNumber, and sectionId
      const sectionId = this.findSectionId(sortedQuestions, index);
      const updated = { 
        ...q, 
        order: index, 
        questionNumber: questionCounter,
        sectionId: sectionId
      };
      questionCounter++;
      return updated;
    });
  }
}

